// MES Application Entry Point
import './services/axiosConfig'; // configure axios interceptors (auth token + 401 redirect)
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store, AppDispatch, RootState } from './store/store';
import { validateToken } from './store/slices/authSlice';

// Layout components
import Layout from './components/Layout/Layout';

// Auth
import Login from './pages/Login/Login';

// Page components
import Dashboard from './pages/Dashboard/Dashboard';
import Production from './pages/Production/Production';
import Quality from './pages/Quality/Quality';
import Maintenance from './pages/Maintenance/Maintenance';
import Planning from './pages/Planning/Planning';
import Analytics from './pages/Analytics/Analytics';
import ShopFloor from './pages/ShopFloor/ShopFloor';

// New MES Modules
import OperatorWorkstation from './pages/OperatorWorkstation';
import MasterData from './pages/MasterData';
import QRManager from './pages/QRManager';
import CustomerOrders from './pages/CustomerOrders/CustomerOrders';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Profile from './pages/Profile/Profile';
import WorkOrderDetail from './pages/WorkOrderDetail/WorkOrderDetail';

// Admin Dashboard
import Admin from './pages/Admin';

// Operator Workstation Terminal (badge-only, public route)
import WorkstationTerminal from './pages/WorkstationTerminal/WorkstationTerminal';

// Kitchen Ops Module
import KitchenOps from './pages/KitchenOps/KitchenOps';
import MealPlanning from './pages/KitchenOps/components/MealPlanning';

// Dispatch + Inventory UI
import Dispatch from './pages/Dispatch/Dispatch';
import InventoryWorkflows from './pages/Inventory/InventoryWorkflows';
import InventoryManagement from './pages/Inventory/InventoryManagement';
import LotGenealogy from './pages/Inventory/LotGenealogy';

// Scheduling
import DispatchList from './pages/Scheduling/DispatchList';

// Regulatory Compliance
import DocumentControl from './pages/DocumentControl/DocumentControl';
import HACCP from './pages/HACCP/HACCP';

// SPC Analytics
import SPCCharts from './pages/SPC/SPCCharts';

// ERP Integration Module
import ERPIntegration from './pages/ERP/ERPIntegration';

// ISA-95 Compliance Report
import ISA95ComplianceReport from './pages/Compliance/ISA95ComplianceReport';

// Scrap Management
import Scrap from './pages/Scrap/Scrap';
import SupplyChainOverview from './pages/SupplyChain/SupplyChainOverview';
import PurchaseOrders from './pages/SupplyChain/PurchaseOrders';
import SalesOrders from './pages/SupplyChain/SalesOrders';
import ModulesDashboard from './pages/Modules/ModulesDashboard';
import DemandForecasting from './pages/DemandForecasting/DemandForecasting';
import SupplyChainRisk from './pages/SupplyChainRisk/SupplyChainRisk';

// Flour Milling Module
import {
  FlourMilling,
  MillingRecipeList,
  MillingRecipeForm,
  MillingRecipeDetail,
  MillingOrderList,
  MillingOrderForm,
  MillingOrderDetail,
  MillingCostConfigList,
  MillingCostConfigForm,
  GrainLotList,
  GrainLotForm,
  SiloDashboard,
  GristRecipeList,
  GristRecipeForm,
  TemperingList,
  TemperingForm,
  QualityTestList,
  QualityTestForm,
  CoaViewer,
  AflatoxinList,
  AflatoxinForm,
  FortificationList,
  FortificationForm,
  DegermingList,
  DegermingForm,
  MillingSchedule,
  MillingScheduleForm,
  MillingAnalytics,
} from './pages/FlourMilling';

// Create MUI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '"Roboto"',
      '"Helvetica"',
      '"Arial"',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

// Route guard — redirects to /login if not authenticated
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Admin-only route guard — redirects non-admins to /dashboard
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('token');
  let userRole = '';
  try {
    const p = JSON.parse(atob((token || '').split('.')[1] || ''));
    userRole = p.role || p.roles?.[0] || '';
  } catch { /* deny */ }

  if (!token || !['admin', 'ADMIN', 'manager', 'MANAGER'].includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

function AppRoutes() {
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((state: RootState) => state.auth.token);

  // Validate stored token on mount
  // On /login: if token exists but we're at login, clear loading state so form is usable
  useEffect(() => {
    if (token) {
      if (window.location.pathname.startsWith('/login')) {
        dispatch({ type: 'auth/validateToken/rejected' }); // clear isLoading
      } else {
        dispatch(validateToken());
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Public kiosk terminal — no MES login required */}
        <Route path="/workstation" element={<WorkstationTerminal />} />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/production" element={<Production />} />
                  <Route path="/quality" element={<Quality />} />
                  <Route path="/maintenance" element={<Maintenance />} />
                  <Route path="/planning" element={<Planning />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/shopfloor" element={<ShopFloor />} />
                  <Route path="/operator-workstation" element={<OperatorWorkstation />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/work-orders/:id" element={<WorkOrderDetail />} />
                  <Route path="/customer-orders" element={<CustomerOrders />} />
                  <Route path="/master-data" element={<MasterData />} />
                  <Route path="/qr-manager" element={<QRManager />} />

                  {/* Admin Routes — restricted to admin/manager roles */}
                  <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                  {/* Supply Chain Routes */}
                  <Route path="/supply-chain" element={<SupplyChainOverview />} />
                  <Route path="/supply-chain/purchase-orders" element={<PurchaseOrders />} />
                  <Route path="/supply-chain/sales-orders" element={<SalesOrders />} />
                  <Route path="/demand-forecasting" element={<DemandForecasting />} />
                  <Route path="/supply-chain-risk" element={<SupplyChainRisk />} />
                  <Route path="/modules" element={<ModulesDashboard />} />

                  {/* Kitchen Ops Routes */}
                  <Route path="/kitchen-ops" element={<KitchenOps />} />
                  <Route path="/kitchen-ops/meal-planning" element={<MealPlanning />} />

                  {/* Flour Milling Routes */}
                  <Route path="/milling" element={<FlourMilling />} />
                  <Route path="/milling/recipes" element={<MillingRecipeList />} />
                  <Route path="/milling/recipes/new" element={<MillingRecipeForm />} />
                  <Route path="/milling/recipes/:id" element={<MillingRecipeDetail />} />
                  <Route path="/milling/recipes/:id/edit" element={<MillingRecipeForm />} />
                  <Route path="/milling/orders" element={<MillingOrderList />} />
                  <Route path="/milling/orders/new" element={<MillingOrderForm />} />
                  <Route path="/milling/orders/:id" element={<MillingOrderDetail />} />
                  <Route path="/milling/orders/:id/edit" element={<MillingOrderForm />} />
                  <Route path="/milling/costs" element={<MillingCostConfigList />} />
                  <Route path="/milling/costs/new" element={<MillingCostConfigForm />} />
                  <Route path="/milling/costs/:id" element={<MillingCostConfigForm />} />
                  <Route path="/milling/costs/:id/edit" element={<MillingCostConfigForm />} />

                  {/* Phase 1: Grain Lots */}
                  <Route path="/milling/grain-lots" element={<GrainLotList />} />
                  <Route path="/milling/grain-lots/new" element={<GrainLotForm />} />
                  <Route path="/milling/grain-lots/:id/edit" element={<GrainLotForm />} />

                  {/* Phase 1: Silos */}
                  <Route path="/milling/silos" element={<SiloDashboard />} />

                  {/* Phase 1: Grist Recipes */}
                  <Route path="/milling/grist-recipes" element={<GristRecipeList />} />
                  <Route path="/milling/grist-recipes/new" element={<GristRecipeForm />} />
                  <Route path="/milling/grist-recipes/:id/edit" element={<GristRecipeForm />} />

                  {/* Phase 1: Tempering */}
                  <Route path="/milling/tempering" element={<TemperingList />} />
                  <Route path="/milling/tempering/new" element={<TemperingForm />} />
                  <Route path="/milling/tempering/:id/edit" element={<TemperingForm />} />

                  {/* Phase 2: Quality Tests & CoA */}
                  <Route path="/milling/quality-tests" element={<QualityTestList />} />
                  <Route path="/milling/quality-tests/new" element={<QualityTestForm />} />
                  <Route path="/milling/quality-tests/:id/edit" element={<QualityTestForm />} />
                  <Route path="/milling/quality-tests/coa/:referenceId" element={<CoaViewer />} />

                  {/* Phase 3: Aflatoxin */}
                  <Route path="/milling/aflatoxin" element={<AflatoxinList />} />
                  <Route path="/milling/aflatoxin/new" element={<AflatoxinForm />} />
                  <Route path="/milling/aflatoxin/:id/edit" element={<AflatoxinForm />} />

                  {/* Phase 3: Fortification */}
                  <Route path="/milling/fortification" element={<FortificationList />} />
                  <Route path="/milling/fortification/new" element={<FortificationForm />} />
                  <Route path="/milling/fortification/:id/edit" element={<FortificationForm />} />

                  {/* Phase 3: Degerming */}
                  <Route path="/milling/degerming" element={<DegermingList />} />
                  <Route path="/milling/degerming/new" element={<DegermingForm />} />
                  <Route path="/milling/degerming/:id/edit" element={<DegermingForm />} />

                  {/* Phase 4: Milling Schedule */}
                  <Route path="/milling/schedule" element={<MillingSchedule />} />
                  <Route path="/milling/schedule/new" element={<MillingScheduleForm />} />
                  <Route path="/milling/schedule/:id/edit" element={<MillingScheduleForm />} />

                  {/* Analytics */}
                  <Route path="/milling/analytics" element={<MillingAnalytics />} />

                  {/* Dispatch + Inventory */}
                  <Route path="/dispatch" element={<Dispatch />} />
                  <Route path="/inventory" element={<InventoryManagement />} />
                  <Route path="/inventory-workflows" element={<InventoryWorkflows />} />
                  <Route path="/inventory/lot-genealogy" element={<LotGenealogy />} />

                  {/* Scheduling */}
                  <Route path="/scheduling/dispatch" element={<DispatchList />} />

                  {/* Regulatory Compliance */}
                  <Route path="/document-control" element={<DocumentControl />} />
                  <Route path="/haccp" element={<HACCP />} />

                  {/* SPC Analytics */}
                  <Route path="/spc" element={<SPCCharts />} />

                  {/* ERP Integration Routes */}
                  <Route path="/erp" element={<ERPIntegration />} />

                  {/* ISA-95 Compliance Report */}
                  <Route path="/compliance/isa95" element={<ISA95ComplianceReport />} />

                  {/* Scrap Management */}
                  <Route path="/scrap" element={<Scrap />} />
                </Routes>
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppRoutes />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
